import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateBrevoIntegration from "@babackapp/private-api-client/client/use-update-brevo-integration";
import useTestBrevoIntegration from "@babackapp/private-api-client/client/use-test-brevo-integration";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";
import IntegrationPreview from "./integration-preview";

const formSchema = z.object({
  activated: z.boolean(),
  token: z.string(),
});

const BrevoIntegration = () => {
  const t = useTranslation();

  const { data: shop } = useCurrentShopSuspense();
  const { activated, token } = shop.integrations.brevo;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { activated, token },
  });
  const { mutateAsync: updateBrevoIntegration } =
    useUpdateBrevoIntegration(shop);

  const { mutateAsync: testBrevoIntegration } = useTestBrevoIntegration(shop);

  const onSubmit = form.handleSubmit(async (data) => {
    return updateBrevoIntegration(data).then(() => form.reset(data));
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col justify-start gap-4">
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.thirdpart.brevo.brevoToken")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <Button type="submit" disabled={!form.formState.isDirty}>
          {t("misc.save")}
        </Button>

        <Button
          type="button"
          disabled={shop.integrations.brevo.token.length <= 0}
          onClick={() => testBrevoIntegration()}
          variant="outline"
        >
          {t("settings.integrations.thirdpart.brevo.initBrevo")}
        </Button>
      </form>
    </Form>
  );
};

export function BrevoIntegrationPreview() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateActivation } =
    useUpdateBrevoIntegration(shop);

  const onActivationUpdate = useCallback(
    (activated: boolean) => {
      if (!shop) return;
      updateActivation({ activated });
    },
    [shop, updateActivation],
  );

  if (!shop || !shop.plan.brevo) return null;

  return (
    <IntegrationPreview
      title="Brevo"
      bgColor="white"
      textColor="black"
      logo="/integrations/brevo.png"
      description={t("settings.integrations.thirdpart.brevo.description")}
      onActivationUpdate={onActivationUpdate}
      activated={shop.integrations.brevo.activated}
      seeMoreUrl="https://www.brevo.com/"
    >
      <BrevoIntegration />
    </IntegrationPreview>
  );
}
